/**
 * RiskFormSlideOver — right-edge slide-over for creating + editing risks.
 * Replaces the existing centred dialog `RiskForm.tsx` with the SSP-style
 * slide-over UX. Same form fields, same dispatch surface (createRisk /
 * updateRisk).
 *
 * The legacy dialog at `components/RiskForm.tsx` stays on disk as an
 * orphan — RiskRegisterPanel and RiskAssessmentWorkspace now import this
 * new file. Cleanup of the old file is deferred to the closing-sweep
 * pass.
 *
 * Linear: MYD-214 (G3).
 *
 * Sidebar IA Lock: this PR does NOT touch AdminPanelCN.tsx.
 */

import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Sheet } from 'components/ui/sheet'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { ShieldAlert, AlertCircle, Loader2 } from 'lucide-react'
import { createRisk, updateRisk } from 'slices/riskSlice'
import PopupCloseButton from 'components/PopupCloseButton'

const STATUSES = ['OPEN', 'MITIGATING', 'ACCEPTED', 'RESOLVED']

interface RiskFormSlideOverProps {
	open: boolean
	onClose: () => void
	evalId: string
	siteId: string
	risk?: any
}

const RiskFormSlideOver = ({
	open,
	onClose,
	evalId,
	siteId,
	risk,
}: RiskFormSlideOverProps) => {
	const dispatch = useDispatch()
	const isEdit = Boolean(risk)

	const [name, setName] = useState('')
	const [category, setCategory] = useState('')
	const [description, setDescription] = useState('')
	const [likelihood, setLikelihood] = useState('3')
	const [impact, setImpact] = useState('3')
	const [mitigation, setMitigation] = useState('')
	const [contingency, setContingency] = useState('')
	const [status, setStatus] = useState('OPEN')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (open) {
			setName(risk?.name || '')
			setCategory(risk?.category || '')
			setDescription(risk?.description || '')
			setLikelihood(String(risk?.likelihood ?? 3))
			setImpact(String(risk?.impact ?? 3))
			setMitigation(risk?.mitigation || '')
			setContingency(risk?.contingency || '')
			setStatus(risk?.status || 'OPEN')
			setSubmitting(false)
			setError(null)
		}
	}, [open, risk])

	const handleSubmit = useCallback(async () => {
		if (!name.trim() || !category.trim()) {
			setError('Name and category are required')
			return
		}
		const lk = Number(likelihood)
		const im = Number(impact)
		if (lk < 1 || lk > 5 || im < 1 || im > 5) {
			setError('Likelihood and impact must be 1–5')
			return
		}
		setError(null)
		setSubmitting(true)
		const payload = {
			name: name.trim(),
			category: category.trim(),
			description: description.trim() || undefined,
			likelihood: lk,
			impact: im,
			mitigation: mitigation.trim() || undefined,
			contingency: contingency.trim() || undefined,
			status,
		}
		try {
			// @ts-ignore — thunks
			if (isEdit) {
				await dispatch(updateRisk({ evalId, siteId, riskId: risk._id, payload }))
			} else {
				// @ts-ignore — thunks
				await dispatch(createRisk({ evalId, siteId, payload }))
			}
			onClose()
		} catch (err: any) {
			setError(err?.message || 'Save failed')
		} finally {
			setSubmitting(false)
		}
	}, [
		dispatch,
		evalId,
		siteId,
		risk,
		isEdit,
		name,
		category,
		description,
		likelihood,
		impact,
		mitigation,
		contingency,
		status,
		onClose,
	])

	const score = Number(likelihood) * Number(impact)
	const severity = score <= 4 ? 'low' : score <= 15 ? 'medium' : 'high'
	const severityChip =
		severity === 'high'
			? 'bg-red-100 text-red-700'
			: severity === 'medium'
				? 'bg-amber-100 text-amber-700'
				: 'bg-emerald-100 text-emerald-700'

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[480px] max-w-[90vw]">
			{/* Header */}
			<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
				<div className="flex items-center gap-2">
					<ShieldAlert className="w-5 h-5 text-[#0080fe]" />
					<h2 className="text-lg font-semibold text-gray-900">
						{isEdit ? 'Edit risk' : 'Add risk'}
					</h2>
				</div>
				<PopupCloseButton onClose={onClose} />
			</div>

			{/* Body */}
			<div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
				<div className="grid grid-cols-12 gap-3">
					<div className="col-span-7">
						<Label htmlFor="riskName" className="mb-1.5 block">
							Name *
						</Label>
						<Input
							id="riskName"
							value={name}
							onChange={(e) => setName(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="col-span-5">
						<Label htmlFor="riskCat" className="mb-1.5 block">
							Category *
						</Label>
						<Input
							id="riskCat"
							value={category}
							onChange={(e) => setCategory(e.target.value)}
							placeholder="e.g. Market, Legal"
						/>
					</div>
				</div>

				<div>
					<Label htmlFor="riskDesc" className="mb-1.5 block">
						Description
					</Label>
					<Input
						id="riskDesc"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
					/>
				</div>

				<div>
					<div className="flex items-center justify-between mb-1.5">
						<Label className="block">Score</Label>
						<span
							className={`text-[10px] px-2 py-0.5 rounded ${severityChip} font-semibold uppercase tracking-wide`}
						>
							{severity} · {score}
						</span>
					</div>
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-4">
							<Label htmlFor="riskLk" className="mb-1.5 block text-xs text-gray-500">
								Likelihood (1–5) *
							</Label>
							<Input
								id="riskLk"
								type="number"
								min={1}
								max={5}
								value={likelihood}
								onChange={(e) => setLikelihood(e.target.value)}
							/>
						</div>
						<div className="col-span-4">
							<Label htmlFor="riskIm" className="mb-1.5 block text-xs text-gray-500">
								Impact (1–5) *
							</Label>
							<Input
								id="riskIm"
								type="number"
								min={1}
								max={5}
								value={impact}
								onChange={(e) => setImpact(e.target.value)}
							/>
						</div>
						<div className="col-span-4">
							<Label htmlFor="riskStatus" className="mb-1.5 block text-xs text-gray-500">
								Status
							</Label>
							<Select
								id="riskStatus"
								value={status}
								onChange={(e: any) => setStatus(e.target.value)}
							>
								{STATUSES.map((s) => (
									<option key={s} value={s}>
										{s}
									</option>
								))}
							</Select>
						</div>
					</div>
				</div>

				<div>
					<Label htmlFor="riskMit" className="mb-1.5 block">
						Mitigation
					</Label>
					<Input
						id="riskMit"
						value={mitigation}
						onChange={(e) => setMitigation(e.target.value)}
						placeholder="Steps to reduce likelihood / impact"
					/>
				</div>
				<div>
					<Label htmlFor="riskCont" className="mb-1.5 block">
						Contingency
					</Label>
					<Input
						id="riskCont"
						value={contingency}
						onChange={(e) => setContingency(e.target.value)}
						placeholder="Plan if risk materialises"
					/>
				</div>

				{error && (
					<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
						<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
						<span>{error}</span>
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
				<Button variant="ghost" onClick={onClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={submitting}
					className="gap-1.5"
				>
					{submitting ? (
						<>
							<Loader2 className="w-4 h-4 animate-spin" />
							Saving…
						</>
					) : isEdit ? (
						'Update risk'
					) : (
						'Add risk'
					)}
				</Button>
			</div>
		</Sheet>
	)
}

export default RiskFormSlideOver
